import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { get, noop } from 'lodash';
import { QuillEditorComponent, QuillFormat } from 'ngx-quill';
import Quill from 'quill';
import { DeltaOperation } from 'quill/index';

import { SelectOption } from '@platform/types';
import { EMMAFormElementComponent } from '../emma-form-element.component';
import { AUTOCOMPLETE_CHECK_TYPE } from '../emma-textarea.component';

enum QUILL_EVENT_SOURCE {
  API = 'api',
  USER = 'user',
  SILENT = 'silent',
}

interface QuillSelectionRange {
  index: number;
  length: number;
}

interface QuillSelectionEvent {
  editor: Quill;
  oldRange?: QuillSelectionRange;
  range?: QuillSelectionRange;
  source: QUILL_EVENT_SOURCE;
}

interface QuillContentEvent {
  editor: Quill;
  content: { ops: DeltaOperation[] };
  delta: { ops: DeltaOperation[] };
  html: string;
  oldDelta: { ops: DeltaOperation[] };
  source: QUILL_EVENT_SOURCE;
  text: string;
}

const EMMA_RICH_EDITOR_CONTROL_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMARichEditorComponent),
  multi: true,
};

const EMMA_RICH_EDITOR_VALIDATOR = {
  provide: NG_VALIDATORS,
  // tslint:disable-next-line: no-use-before-declare
  useExisting: forwardRef(() => EMMARichEditorComponent),
  multi: true,
};

@Component({
    selector: 'emma-rich-editor',
    templateUrl: './emma-rich-editor.component.html',
    providers: [
        EMMA_RICH_EDITOR_CONTROL_VALUE_ACCESSOR,
        EMMA_RICH_EDITOR_VALIDATOR,
        {
            provide: EMMAFormElementComponent,
            useExisting: EMMARichEditorComponent,
        },
    ],
    standalone: false
})
export class EMMARichEditorComponent
  extends EMMAFormElementComponent
  implements OnInit, OnChanges, ControlValueAccessor, Validator
{
  @ViewChild(QuillEditorComponent, { static: true })
  quill!: QuillEditorComponent;
  @Input() autocompleteItems: SelectOption[] = [];
  @Input() autocompleteButtonLabel = '';
  @Input() autocompleteButtonIcon = '';
  @Input() autocompleteNotFound = '';
  @Input() autocompleteCheckItems = false;
  @Input() autocompleteCheckItemsModalType = AUTOCOMPLETE_CHECK_TYPE.AUDIENCE_TAGS;
  @Input() autocompleteCheckIcon = '';
  @Input() autocompleteCheckPopoverMsg = '';
  @Input() autocompleteGroupBy?: string;

  @Input() format: QuillFormat = 'html';
  @Input() sanitize = this.format === 'html';
  @Input() minlength = 0;
  @Input() maxlength = Infinity;
  @Output() contentChanged = new EventEmitter<QuillContentEvent>();

  override _containerClass = ['emma-rich-editor'];

  selectionRange: QuillSelectionRange = { index: 0, length: 0 };

  insertImageTagModal = false;

  // Form
  @Input()
  get value(): string {
    return this._innerValue;
  }
  set value(v: string) {
    if (v !== this._innerValue) {
      this._innerValue = v;
      this.onChangeCallback(v);
    }
  }
  private _innerValue = '';
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: string | number) => void = noop;

  showCheckModal = false;

  onInsertText = (insert: string | number) => {
    const text = String(insert);
    const quillEditor: Quill = this.quill.quillEditor;
    if (text) {
      const { index, length } = this.selectionRange;
      if (length) {
        quillEditor.deleteText(index, length, QUILL_EVENT_SOURCE.USER);
      }
      quillEditor.insertText(index, text, QUILL_EVENT_SOURCE.USER);
      this.selectionRange = { index: index + text.length, length: 0 };
      quillEditor.setSelection(this.selectionRange.index, this.selectionRange.length);
    }
  };

  onBlur() {
    this.onTouchedCallback();
  }

  onContentChanged(event: QuillContentEvent) {
    this.contentChanged.emit(event);
  }

  onSelectionChanged(event: QuillSelectionEvent) {
    const range = get(event, 'range');
    if (range) {
      this.selectionRange = { ...range };
    }
  }

  onSelectAutocompleteTag(tag: string) {
    // Elimina el espacio al final si es una substitución
    this.onInsertText(this.selectionRange.length ? tag.trim() : tag);
  }

  onInsertImageTag(src: string) {
    const quillEditor: Quill = this.quill.quillEditor;
    const { index, length } = this.selectionRange;
    if (length) {
      quillEditor.deleteText(index, length, QUILL_EVENT_SOURCE.USER);
    }
    quillEditor.insertEmbed(this.selectionRange.index, 'image', src, QUILL_EVENT_SOURCE.USER);
    this.selectionRange = { index, length: 0 };
    quillEditor.setSelection(this.selectionRange.index, this.selectionRange.length);
  }

  // From ControlValueAccessor interface
  writeValue(value: string) {
    if (value !== this._innerValue) {
      this._innerValue = value;
    }
  }

  // From ControlValueAccessor interface
  registerOnChange(fn: (_: string | number) => void) {
    this.onChangeCallback = fn;
  }

  // From ControlValueAccessor interface
  registerOnTouched(fn: () => void) {
    this.onTouchedCallback = fn;
  }

  // From Validator interface
  override validate: ValidatorFn = (control: AbstractControl) => {
    const validators: ValidatorFn[] = [];
    if (this.required) {
      validators.push(Validators.required);
    }
    if (this.maxlength < Infinity) {
      validators.push(Validators.maxLength(this.maxlength));
    }
    if (this.minlength) {
      validators.push(Validators.minLength(this.minlength));
    }
    if (this.validateFn) {
      validators.push(this.validateFn);
    }
    if (validators.length) {
      const validator = Validators.compose(validators);
      return validator ? validator(control) : null;
    }
    return null;
  };

  onCheckItems(): void {
    this.showCheckModal = true;
  }

  onCancelCheckItems(): void {
    this.showCheckModal = false;
  }
}
